import {ChangeDetectorRef, inject, Injectable, Injector, OnDestroy, Pipe, PipeTransform} from '@angular/core';
import {AsyncPipe} from '@angular/common';
import {Store} from '@ngrx/store';
import {MxSelector} from '../store/store.types';
import {isDefined} from '../store/common/typing.helpers';
import {Observable} from 'rxjs';

@Injectable({providedIn: 'root'})
@Pipe({
  name: 'select',
  pure: false // eslint-disable-line @angular-eslint/no-pipe-impure -- This pipe wraps the async pipe, which is also impure.
})
/**
 * Given a selector, selects it from the store and runs it through Angular's async pipe.  Also converts any `null` value coming from `async` to `undefined`
 * instead.  An optional fallback parameter can be used to replace `undefined` if desired (or if needed -- see below [*]).
 * An undefined selector returns the fallback (or undefined) and releases any previous subscription.
 * <hr/>
 * Usage examples:
 *
 * Given a container defined in the component like `public selectors = PatronsSelectors.Current;`:
 * <ul>
 *   <li><code>&lt;span>{{ selectors.id | select }}&lt;/span></code></li>
 *   <li><code>&lt;span *ngIf="selectors.patronNameIs('Fred') | select">Fred is here!&lt;/span></code></li>
 *   <li><code>&lt;span>{{ selectors.phone | select:'' | phone }}&lt;/span></code></li>
 * </ul>
 * [*] For the last example, the `selectors.phone | select` is incorrectly marked as a type error by IntelliJ's `JavaScript and TypeScript` plugin.  We can give
 * it a hint by supplying a fallback value of the correct type (`''` is a `string`).  Unfortunately, we need to do this even if the selector already prevents
 * runtime errors with its own fallback.
 */
export class SelectPipe implements PipeTransform, OnDestroy {
  /*
    eslint-disable-next-line @angular-eslint/prefer-inject -- AsyncPipe needs a ChangeDetectorRef, and the only way to resolve it is via the old-fashioned
    constructor argument.  inject() won't work here, whether for Injector or ChangeDetectorRef.  The result for both is a "No provider found" runtime error.
  */
  constructor(injector: Injector) {
    this.asyncPipe = new AsyncPipe(injector.get(ChangeDetectorRef));
  }

  public ngOnDestroy(): void {
    this.asyncPipe.ngOnDestroy(); // eslint-disable-line @angular-eslint/no-lifecycle-call -- We must prevent subscription leaks in the async pipe.
  }

  public transform<T>(selector: MxSelector<T> | undefined): T | undefined;
  public transform<T>(selector: MxSelector<T> | undefined, asyncNullFallbackValue: T): T;
  public transform<T>(selector: MxSelector<T> | undefined, asyncNullFallbackValue?: T): T | undefined {
    // Passing null to the async pipe also unsubscribes from the previous selector when the input becomes undefined.
    const source: Observable<T> | null = isDefined(selector) ? this.store.select(selector) : null;
    return this.asyncPipe.transform(source) ?? asyncNullFallbackValue;
  }

  private asyncPipe: AsyncPipe;
  private store: Store = inject(Store);
}
